import { usePlaidLink } from "react-plaid-link";
import ToggleSwitch from "../modules/customer/pages/ToggleSwitch";
import {
  LockClosedIcon,
  CheckCircleIcon as FooterCheckCircleIcon,
} from "./Icons";
import {
  ApplicationStatusDto,
  DropdownFieldProps,
  InputFieldProps,
  LabelledToggleSwitchProps,
  ReadOnlyFieldProps,
  SectionHeaderProps,
} from "./types";
import { useEffect } from "react";
import { H_MBN_CONTENT_REM, H_QJN_REM } from "./helperConstants";

export const PageHeader: React.FC = () => {
  return (
    <header className="bg-white text-gray-700 border-b border-gray-200">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between">
          {/* Left section for menu button */}
          <div className="flex-1 flex justify-start">
            <img
              src="media/images/veros-logo-print-copy.png"
              alt="veros-logo-print-copy"
              className="w-auto hidden md:block"
              style={{ height: "41px", border: "0" }}
            />
          </div>

          {/* Centered CustomerLane Logo */}
          <div className="flex-shrink-0">
            <img
              src="media/images/Screenshot-2025-05-28-100429.png"
              alt="CustomerLane Logo"
              className="block w-auto h-auto max-h-[45px] md:max-h-[70px]"
            />
          </div>

          {/* Right Aligned: Veros Credit Logo */}
          <div className="flex-1 flex justify-end"></div>
        </div>
      </div>
    </header>
  );
};

export const PageFooter: React.FC = () => {
  return (
    <footer className="bg-white border-t border-gray-200 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-row flex-wrap justify-center items-center text-sm text-gray-700 gap-x-6 gap-y-3">
          <div className="flex items-center">
            <LockClosedIcon className="w-5 h-5 text-blue-600 mr-2" />
            <span>Private and Secure</span>
          </div>
          <div className="flex items-center">
            <FooterCheckCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
            <span>Faster Processing</span>
          </div>
          <div className="flex items-center">
            <FooterCheckCircleIcon className="w-5 h-5 text-blue-600 mr-2" />
            <span>Convenient</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const LabelledToggleSwitch: React.FC<LabelledToggleSwitchProps> = ({
  label,
  ...toggleProps
}) => (
  <div className="flex justify-between items-center py-2.5 border-b border-gray-200 last:border-b-0">
    <span className="text-base leading-4 text-gray-700">{label}</span>
    <ToggleSwitch {...toggleProps} />
  </div>
);

export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  toggleId,
  toggleChecked,
  onToggleChange,
}) => (
  <div className="bg-[#09517d] text-white p-3 rounded-t-lg flex justify-between items-center">
    <h3 className="text-base leading-4 font-semibold">{title}</h3>
    <ToggleSwitch
      id={toggleId}
      checked={toggleChecked}
      onChange={onToggleChange}
      labelYes="YES"
      labelNo="NO"
      overrideLabelNoColor="text-white"
      overrideLabelYesColor="text-white"
    />
  </div>
);

export const formatUSD = (value: string) => {
  if (!value) return "";

  const [intPart, decPart] = value.split(".");
  const formattedInt = Number(intPart).toLocaleString("en-US");

  return decPart !== undefined
    ? `$${formattedInt}.${decPart}`
    : `$${formattedInt}`;
};

export const sanitizeUSDInput = (value: string) => {
  const clean = value.replace(/[$,]/g, "");

  if (!/^\d*\.?\d*$/.test(clean)) return null;

  const [intPart, decPart = ""] = clean.split(".");

  if (intPart.length > 6) return null;
  if (decPart.length > 2) return null;

  return clean;
};

export const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  label,
  value,
}) => (
  <div className="flex flex-row justify-between items-baseline py-2.5 border-b border-gray-200 last:border-b-0 md:py-3">
    <span className="text-base leading-4 text-gray-600 mr-2">{label}</span>
    <span className="text-base leading-4 text-gray-800 font-medium text-right">
      {value}
    </span>
  </div>
);

export const InputField: React.FC<InputFieldProps> = ({
  label,
  id,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  disabled,
  maxLength,
  labelColor = "text-gray-700",
}) => (
  <div className="py-2">
    <label
      htmlFor={id}
      className={`block text-base leading-4 font-medium ${labelColor} mb-1`}
    >
      {label}
    </label>
    <input
      type={type}
      id={id}
      name={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      maxLength={maxLength}
      className="mt-0.5 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base leading-4 disabled:bg-gray-100"
    />
  </div>
);

export const InputAmountField: React.FC<InputFieldProps> = ({
  label,
  id,
  value,
  onChange,
  required,
  disabled,
  labelColor = "text-gray-700",
}) => (
  <div className="py-2">
    <label
      htmlFor={id}
      className={`block text-base leading-4 font-medium ${labelColor} mb-1`}
    >
      {label}
    </label>
    <input
      type="text"
      id={id}
      name={id}
      value={formatUSD(value)}
      onChange={(e) => {
        const sanitized = sanitizeUSDInput(e.target.value);
        if (sanitized !== null) {
          onChange(sanitized); // store raw number only
        }
      }}
      placeholder="$0.00"
      inputMode="decimal"
      disabled={disabled}
      required={required}
      className="mt-0.5 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base leading-4 disabled:bg-gray-100"
    />
  </div>
);

// Internal DropdownField component

export const DropdownField: React.FC<DropdownFieldProps> = ({
  label,
  id,
  value,
  onChange,
  options,
  disabled,
}) => (
  <div className="py-2">
    <label
      htmlFor={id}
      className="block text-base leading-4 font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <select
      id={id}
      name={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="mt-0.5 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-base leading-4 disabled:bg-gray-100"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

export const getMimetype = async (signature: string) => {
  switch (signature) {
    case "FFD8FFDB":
    case "FFD8FFE0":
    case "FFD8FFE1":
    case "FFD8FFE2":
      return "image/jpeg";

    case "89504E47":
      return "image/png";

    case "47494638":
      return "image/gif";

    case "25504446":
      return "application/pdf";

    //Match the string that starts with 424D
    case signature.match(/^424D/)?.input:
      return "image/bmp";

    case "00018667":
      return "image/heic";

    default:
      return "Invalid format";
  }
};

export const checkMIME = async (img: any) => {
  // we need to get the raw bytes
  const buffer = await img.arrayBuffer();
  // each entry of array should contain 8 bits
  const bytes = new Uint8Array(buffer);

  let allBytes: string[] = [];
  bytes.forEach((byte) => {
    allBytes.push(byte.toString(16));
  });

  const hex = allBytes.join("").toUpperCase();
  const type = await getMimetype(hex.substring(0, 8));
  return type;
};

export const PlaidLinkWrapper = ({
  token,
  onSuccess,
  onExit,
  onEvent,
}: any) => {
  const { open, ready } = usePlaidLink({
    token,
    onSuccess,
    onExit,
    onEvent,
  });

  useEffect(() => {
    if (ready) open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, open]);

  return null;
};

// Get inviteId from URL params
export const getInviteId = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get("invite") || "";
};

export const getCustomerEmail = (
  response: ApplicationStatusDto,
  isCoApplicant: boolean,
): string =>
  isCoApplicant
    ? response.coApplicant?.email || ""
    : response.customer?.email || "";

export const deriveCompletionStatus = (
  response: ApplicationStatusDto,
  isCoApplicant: boolean,
): Record<number, boolean> => {
  const completionStatus: Record<number, boolean> = {};

  if (!isCoApplicant) {
    if (response.steps?.insurance) {
      completionStatus[2] = response.steps.insurance.completed || false;
    }
    if (response.steps?.questionnaire) {
      completionStatus[5] = response.steps.questionnaire.completed || false;
    }
  }

  if (response.steps?.driversLicense) {
    completionStatus[1] = response.steps.driversLicense.completed || false;
  }

  if (response.steps?.bank || response.steps?.payroll) {
    completionStatus[4] =
      response.steps?.bank?.completed ||
      response.steps?.payroll?.completed ||
      false;
  }

  if (response.steps?.residence) {
    completionStatus[6] = response.steps.residence.completed || false;
  }

  if (response.steps?.references) {
    completionStatus[3] = response.steps.references.completed || false;
  }

  return completionStatus;
};

export const getMobileContentPaddingClass = (
  isMobileBottomNavActive: boolean,
  isQuickJumpNavVisible: boolean,
): string => {
  if (isMobileBottomNavActive && isQuickJumpNavVisible) {
    return `pb-[calc(${H_QJN_REM}rem+${H_MBN_CONTENT_REM}rem+env(safe-area-inset-bottom))]`;
  }
  if (isMobileBottomNavActive) {
    return `pb-[calc(${H_MBN_CONTENT_REM}rem+env(safe-area-inset-bottom))]`;
  }
  if (isQuickJumpNavVisible) {
    return `pb-[calc(${H_QJN_REM}rem+env(safe-area-inset-bottom))]`;
  }
  return "pb-0";
};
